import { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { api } from '../api';
import { useAuth } from '../context/AuthContext';
import { genreLabel } from '../constants/genreLabels';
import AnimeStatusSelector from '../components/AnimeStatusSelector';
import AnimeCard from '../components/AnimeCard';
import ScrollRow from '../components/ScrollRow';
import RatingBadge from '../components/RatingBadge';
import { statusLabel } from '../constants/statusLabels';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000/api';
const WATCHED_KEY = 'ashveil_watched';

const getWatchedLocal = () => {
  try { return new Set(JSON.parse(localStorage.getItem(WATCHED_KEY) || '[]')); }
  catch { return new Set(); }
};

const browseFetch = (params) =>
  fetch(`${API_URL}/anime/browse?${new URLSearchParams(params)}`)
    .then((r) => r.json())
    .then((d) => d.results || [])
    .catch(() => []);

export default function AnimePage() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [anime,        setAnime]        = useState(null);
  const [loading,      setLoading]      = useState(true);
  const [error,        setError]        = useState(null);
  const [watched,      setWatched]      = useState(new Set());
  const [animeStatus,  setAnimeStatus]  = useState(undefined);
  const [recs,         setRecs]         = useState({ genre: [], year: [], rating: [] });
  const [related,      setRelated]      = useState(null);
  const [activeTab,    setActiveTab]    = useState(null);

  useEffect(() => {
    setLoading(true);
    api.anime.get(id)
      .then(setAnime)
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [id]);

  // Load watched episodes
  useEffect(() => {
    if (user) {
      const token = localStorage.getItem('token');
      fetch(`${API_URL}/user/watched`, {
        headers: { Authorization: `Bearer ${token}` },
      })
        .then((r) => r.json())
        .then((ids) => Array.isArray(ids) && setWatched(new Set(ids)))
        .catch(() => {});
    } else {
      setWatched(getWatchedLocal());
    }
  }, [user]);

  // Load current anime status (for status selector)
  useEffect(() => {
    if (!user) { setAnimeStatus(null); return; }
    const token = localStorage.getItem('token');
    fetch(`${API_URL}/user/anime-status/${encodeURIComponent(id)}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.json())
      .then((d) => setAnimeStatus(d.status || null))
      .catch(() => setAnimeStatus(null));
  }, [id, user]);

  // Load recommendations after anime data is ready
  useEffect(() => {
    if (!anime) return;
    const genre  = anime.genres?.[0];
    const year   = anime.releaseDate;

    const promises = [
      genre ? browseFetch({ genres: genre, page: 1 }) : Promise.resolve([]),
      year  ? browseFetch({ year, page: 1 })          : Promise.resolve([]),
      browseFetch({ order: 'rating', page: 1 }),
    ];

    Promise.all(promises).then(([genreRes, yearRes, ratingRes]) => {
      const exclude = (list) => list.filter((a) => a.id !== id).slice(0, 20);
      setRecs({
        genre:  exclude(genreRes),
        year:   exclude(yearRes),
        rating: exclude(ratingRes),
      });
    });
  }, [anime, id]);

  // Load related animes
  useEffect(() => {
    if (!id) return;
    fetch(`${API_URL}/anime/${encodeURIComponent(id)}/related`)
      .then((r) => r.json())
      .then(setRelated)
      .catch(() => {});
  }, [id]);

  // Auto-select first meaningful tab
  useEffect(() => {
    if (!related) return;
    if (related.temporadas?.length > 1)    setActiveTab('temporadas');
    else if (related.movies?.length > 0)   setActiveTab('movies');
    else if (related.sideStories?.length > 0) setActiveTab('sideStories');
    else if (related.others?.length > 0)   setActiveTab('others');
    else setActiveTab(null);
  }, [related]);

  if (loading) return <div className="page"><div className="state-center">Cargando...</div></div>;
  if (error)   return <div className="page"><div className="state-center state-error">Error: {error}</div></div>;
  if (!anime)  return <div className="page"><div className="state-center">Anime no encontrado.</div></div>;

  const sortedEps   = [...(anime.episodes || [])].sort((a, b) => (a.number || 0) - (b.number || 0));
  const firstEp     = sortedEps[0];
  const watchedEps  = sortedEps.filter((ep) => watched.has(ep.id));
  const lastWatched = watchedEps.length > 0 ? watchedEps[watchedEps.length - 1] : null;
  const nextEp      = lastWatched
    ? (sortedEps.find((ep) => ep.number > lastWatched.number) || lastWatched)
    : firstEp;

  const watchLink = (ep) =>
    `/watch/${encodeURIComponent(ep.id)}?title=${encodeURIComponent(`${anime.title} — Ep ${ep.number}`)}&animeId=${id}&ep=${ep.number}`;

  const thumbSrc = (ep) => ep.thumbnail || anime.image || null;

  return (
    <div className="page">
      {/* ── Banner ── */}
      <section className="abanner">
        {(anime.backdrop_image || anime.banner_image || anime.image) && (
          <img
            className={`abanner__bg${anime.backdrop_image ? ' abanner__bg--wide' : ''}`}
            src={anime.backdrop_image || anime.banner_image || anime.image}
            alt=""
            aria-hidden="true"
            referrerPolicy="no-referrer"
          />
        )}
        <div className="abanner__vignette" />
        <div className="abanner__content">
          {anime.image && (
            <div className="acover">
              <img src={anime.image} alt={anime.title} referrerPolicy="no-referrer" />
            </div>
          )}
          <div className="ainfo">
            <h1 className="ainfo__title">{anime.title}</h1>
            <div className="ainfo__meta">
              {anime.releaseDate && <span>{anime.releaseDate}</span>}
              {anime.status && (
                <><span className="hero__dot">·</span><span>{statusLabel(anime.status)}</span></>
              )}
              {anime.totalEpisodes && (
                <><span className="hero__dot">·</span><span>{anime.totalEpisodes} eps</span></>
              )}
              {anime.duration && (
                <><span className="hero__dot">·</span><span>{anime.duration}</span></>
              )}
              {anime.studio && (
                <><span className="hero__dot">·</span><span>{anime.studio}</span></>
              )}
              {anime.mal_score && <RatingBadge score={anime.mal_score} />}
            </div>

            {/* Status selector — expanded mode */}
            <AnimeStatusSelector
              animeId={id}
              currentStatus={animeStatus}
              onStatusChange={setAnimeStatus}
              expanded
            />

            {anime.genres?.length > 0 && (
              <div className="ainfo__chips">
                {anime.genres.map((g) => <span key={g} className="chip">{genreLabel(g)}</span>)}
              </div>
            )}
            {anime.description && (
              <p className="ainfo__desc">{anime.description}</p>
            )}
            <div className="ainfo__actions">
              {nextEp && (
                <Link to={watchLink(nextEp)} className="btn-play">
                  {lastWatched ? `▶ Continuar Ep ${nextEp.number}` : '▶ Ver E-1'}
                </Link>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* ── Episodes ── */}
      <div className="abody">
        <h2 className="section-head">Episodios ({sortedEps.length})</h2>

        {sortedEps.length === 0 ? (
          <p style={{ color: 'var(--text-sec)' }}>No hay episodios disponibles.</p>
        ) : (
          <div className="ep-cards-grid">
            {sortedEps.map((ep) => {
              const isWatched  = watched.has(ep.id);
              const isContinue = lastWatched?.id === ep.id;
              const thumb      = thumbSrc(ep);
              return (
                <Link
                  key={ep.id}
                  to={watchLink(ep)}
                  className={`ep-card${isWatched ? ' ep-card--watched' : ''}`}
                >
                  <div className="ep-card__thumb">
                    {thumb ? (
                      <img
                        className="ep-card__img"
                        src={thumb}
                        alt=""
                        loading="lazy"
                        referrerPolicy="no-referrer"
                      />
                    ) : (
                      <div className="ep-card__no-thumb">▶</div>
                    )}
                    <div className="ep-card__overlay">
                      <div className="ep-card__play">{isWatched ? '✓' : '▶'}</div>
                    </div>
                    {isWatched && <div className="ep-card__watched-badge">✓</div>}
                    {isContinue && <div className="ep-card__continue-badge">Continuar</div>}
                    <span className="ep-card__num">Ep {ep.number}</span>
                    {ep.duration && (
                      <span className="ep-card__duration">{ep.duration}</span>
                    )}
                  </div>
                  <div className="ep-card__body">
                    <p className="ep-card__title">{ep.title}</p>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </div>

      {/* ── Contenido relacionado ── */}
      {related && activeTab && (
        <div className="anime-related abody">
          <div className="related-tabs">
            {related.temporadas?.length > 1 && (
              <button
                className={`related-tab${activeTab === 'temporadas' ? ' related-tab--active' : ''}`}
                onClick={() => setActiveTab('temporadas')}
              >
                Temporadas ({related.temporadas.length})
              </button>
            )}
            {related.movies?.length > 0 && (
              <button
                className={`related-tab${activeTab === 'movies' ? ' related-tab--active' : ''}`}
                onClick={() => setActiveTab('movies')}
              >
                Películas ({related.movies.length})
              </button>
            )}
            {related.sideStories?.length > 0 && (
              <button
                className={`related-tab${activeTab === 'sideStories' ? ' related-tab--active' : ''}`}
                onClick={() => setActiveTab('sideStories')}
              >
                OVAs / Especiales ({related.sideStories.length})
              </button>
            )}
            {related.others?.length > 0 && (
              <button
                className={`related-tab${activeTab === 'others' ? ' related-tab--active' : ''}`}
                onClick={() => setActiveTab('others')}
              >
                Relacionados ({related.others.length})
              </button>
            )}
          </div>

          <div className="related-content">
            {activeTab === 'temporadas' && (
              <div className="temporadas-grid">
                {related.temporadas.map((t) => (
                  <div
                    key={t.id}
                    className={`temporada-card${t.isCurrent ? ' temporada-card--current' : ''}`}
                    onClick={() => !t.isCurrent && navigate(`/anime/${t.id}`)}
                  >
                    {t.image && (
                      <img
                        src={t.image}
                        alt={t.title}
                        className="temporada-card-img"
                        referrerPolicy="no-referrer"
                        loading="lazy"
                      />
                    )}
                    <div className="temporada-card-info">
                      <span className="temporada-card-title">{t.title}</span>
                      {t.mal_score && (
                        <span className="temporada-card-score">★ {t.mal_score}</span>
                      )}
                      {t.isCurrent && (
                        <span className="temporada-card-current-badge">Aquí estás</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
            {activeTab !== 'temporadas' && (
              <div className="results-grid">
                {(related[activeTab] || []).map((a) => (
                  <AnimeCard key={a.id} anime={a} />
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Recomendaciones ── */}
      {recs.genre.length > 0 && (
        <ScrollRow title={`Más de ${genreLabel(anime.genres?.[0]) || 'este género'}`} items={recs.genre} />
      )}
      {recs.year.length > 0 && anime.releaseDate && (
        <ScrollRow title={`Del mismo año (${anime.releaseDate})`} items={recs.year} />
      )}
      {recs.rating.length > 0 && (
        <ScrollRow title="Mejor valorados" items={recs.rating} />
      )}
    </div>
  );
}
